import { useState } from 'react';
import { CorelWorks } from '../../lib/corel/corelworks';
import { logger } from '../../lib/terminalLogger';

type ColorMode = 'CMYK' | 'RGB';
type Background = 'solid' | 'transparent';

export function OneBitmapPage() {
  const [dpi, setDpi] = useState<string>('400');
  const [colorMode, setColorMode] = useState<ColorMode>('CMYK');
  const [background, setBackground] = useState<Background>('transparent');

  // Função de validação dos campos
  const validateFields = () => {
    const errors: string[] = [];

    // Validar DPI
    const dpiValue = parseInt(dpi);
    if (isNaN(dpiValue) || dpiValue < 72 || dpiValue > 1200) {
      errors.push('DPI deve estar entre 72 e 1200');
    }

    // Validar colorMode
    if (!['CMYK', 'RGB'].includes(colorMode)) {
      errors.push('Modo de cor deve ser CMYK, RGB ou GRAYSCALE');
    }

    // Validar background
    if (!['solid', 'transparent'].includes(background)) {
      errors.push('Fundo deve ser solid ou transparent');
    }


    return errors;
  };

  // Função para aplicar a conversão de bitmap
  const handleApply = async () => {
    // Validar campos
    const errors = validateFields();
    if (errors.length > 0) {
      alert('Erros de validação:\n' + errors.join('\n'));
      return;
    }

    // Preparar opções seguindo o contrato da API
    const options = {
      dpi: parseInt(dpi),
      colorMode,
      transparent: background === 'transparent'
    } as const;

    try {
      logger.debug('Aplicando OneBitmap:', { options });
      
      // Executar a ferramenta Bitmap.Convert.Simple (sempre usa seleção atual)
      const result = await CorelWorks.executeBitmapConvert(options);
      
      if (result.status !== 'ok' && result.Status !== 'ok') {
        alert('Erro ao converter para bitmap. Verifique se há objetos selecionados.');
      }
      // Sucesso: ferramenta silenciosa, sem mensagem
    } catch (error) {
      logger.error('Erro ao converter para bitmap:', error);
      alert('Erro ao converter para bitmap. Verifique se há objetos selecionados.');
    }
  };

  return (
    <div className="h-full flex flex-col">
      {/* UI: Container principal sem scroll */}
      <div className="flex-1 p-3">
        {/* UI: Parâmetros */}
        <section className="bg-navy-800/60 border border-navy-700 rounded-xl p-3 mb-3">
          <div className="text-sm text-gray-300 font-medium mb-3">Parâmetros</div>

          {/* UI: Campos em sequência vertical */}
          <div className="space-y-3">
            <div>
              <label className="block text-xs text-gray-300 mb-1">DPI</label>
              <input 
                type="number"
                min="72"
                max="1200"
                value={dpi} 
                onChange={(e) => setDpi(e.target.value)} 
                placeholder="Ex: 400" 
                className="w-full bg-navy-800 border border-navy-700 rounded px-2 py-1.5 text-sm text-gray-200" 
              />
            </div>
            
            <div>
              <label className="block text-xs text-gray-300 mb-1">Modo de Cor</label>
              <select 
                value={colorMode} 
                onChange={(e) => setColorMode(e.target.value as ColorMode)} 
                className="w-full bg-navy-800 border border-navy-700 rounded px-2 py-1.5 text-sm text-gray-200"
              >
                <option value="CMYK">CMYK</option>
                <option value="RGB">RGB</option>
              </select>
            </div>
            
            <div>
              <label className="block text-xs text-gray-300 mb-1">Fundo</label>
              <select 
                value={background} 
                onChange={(e) => setBackground(e.target.value as Background)} 
                className="w-full bg-navy-800 border border-navy-700 rounded px-2 py-1.5 text-sm text-gray-200"
              >
                <option value="transparent">Transparente</option>
                <option value="solid">Sólido</option>
              </select>
            </div>
          </div>

        </section>

        {/* UI: Informações */}
        <section className="bg-navy-800/60 border border-navy-700 rounded-xl p-3 mb-3">
          <div className="text-sm text-gray-300 font-medium mb-2">Informações</div>
          
          <div className="text-xs text-gray-400 space-y-1">
            <div>• <strong>Seleção:</strong> Converte apenas objetos selecionados</div>
            <div>• <strong>DPI:</strong> Resolução do bitmap gerado (72-1200)</div>
            <div>• <strong>Fundo transparente:</strong> Preserva transparência</div>
            <div>• <strong>Modo CMYK:</strong> Para impressão profissional</div>
            <div>• <strong>Modo RGB:</strong> Para uso digital/tela</div>
          </div>
        </section>
      </div>

      {/* UI: Botão Aplicar - sempre visível */}
      <div className="sticky bottom-[44px] bg-navy-900 border-t border-navy-700 p-3">
        <button 
          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
          onClick={handleApply}
        >
          Aplicar
        </button>
      </div>
    </div>
  );
}
